import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useMemo } from "react";
import { SimulatorPickActionCell } from "@/components/Reports/SimulatorPickActionCell";
import { DataTable } from "@/components/ui/data-table";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  ConditionAnalysisSituationClause,
  ConditionAnalysisSituationRow,
} from "../../../../utils/conditionAnalysisSelectors";
import { pnlBarFill } from "../../Statistics/components/statsSignedPnlBarUtils";
import { Button } from "@/components/ui/button";
import { TradeLogSortableCell } from "../../TradeLog/components/tradeLogSortableCell";
import {
  AddToSimulatorColumnHeader,
  MarketContextActionColumnCell,
  MarketContextPreviewTradesButton,
  MarketContextPreviewTradesColumnHeader,
  MarketContextTooltipContent,
  fmtInt,
  fmtNum,
  fmtPct,
  fmtPnl,
  MarketContextMetricColumnHeader,
} from "./marketContextDataTableShared";
import { marketContextVariantTooltipText } from "./marketContextVariantTooltipText";
import { WIN_RATE_COLUMN_TOOLTIP } from "./marketContextTableTooltips";

function parseSituationStringToClauses(rawSituation: string): ConditionAnalysisSituationClause[] {
  return rawSituation
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const eqIndex = part.indexOf("=");
      if (eqIndex < 0) return { key: part, value: "" };
      return {
        key: part.slice(0, eqIndex).trim(),
        value: part.slice(eqIndex + 1).trim(),
      };
    })
    .filter((c) => c.key.length > 0 || c.value.length > 0);
}

function situationClausesForRow(row: ConditionAnalysisSituationRow): ConditionAnalysisSituationClause[] {
  if (row.clauses?.length) return row.clauses;
  const raw = String(row.situation ?? "").trim();
  if (!raw || raw === "—") return [];
  return parseSituationStringToClauses(raw);
}

function situationRowId(row: ConditionAnalysisSituationRow): string {
  const clauses = situationClausesForRow(row);
  const sig = clauses
    .map((c) => `${c.key}=${c.value}`)
    .filter((x) => x !== "=")
    .sort()
    .join("|");
  const sample = row.sample_size ?? "";
  return `${String(row.situation ?? "s")}::${sig}::${sample}`;
}

function SituationLabelCell(props: {
  row: ConditionAnalysisSituationRow;
  metricDefinitions?: Record<string, unknown>;
}): React.ReactElement {
  const clauses = situationClausesForRow(props.row);
  const raw = String(props.row.situation ?? "").trim();
  const tooltipLabelClass =
    "cursor-help text-sm font-medium text-gray-500 dark:text-sidebar-foreground font-[family-name:var(--font-primary)] normal-case underline decoration-dotted underline-offset-2";

  if (!clauses.length) {
    return (
      <TradeLogSortableCell
        data-market-context-wrap-cell=""
        className="min-w-[16rem] whitespace-normal break-words font-medium"
      >
        {raw || "—"}
      </TradeLogSortableCell>
    );
  }

  return (
    <TradeLogSortableCell
      data-market-context-wrap-cell=""
      className="min-w-[16rem] whitespace-normal break-words"
    >
      <span className="inline-flex flex-wrap items-center gap-0 font-medium">
        {clauses.map((clause, clauseIdx) => (
          <React.Fragment key={`${clause.key}-${clause.value}-${clauseIdx}`}>
            {clause.key ? (
              (() => {
                const tooltipText = marketContextVariantTooltipText(
                  clause.key,
                  clause.value,
                  props.metricDefinitions,
                );
                const clauseLabel = `${clause.key}=${clause.value}`;
                if (!tooltipText) return <span>{clauseLabel}</span>;
                return (
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={tooltipLabelClass} data-no-row-click="">
                          {clauseLabel}
                        </span>
                      </TooltipTrigger>
                      <MarketContextTooltipContent widthClass="max-w-[360px]">
                        {tooltipText}
                      </MarketContextTooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })()
            ) : (
              <span>{clause.value || "—"}</span>
            )}
            {clauseIdx < clauses.length - 1 ? (
              <span className="px-1.5 text-muted-foreground" aria-hidden>
                |
              </span>
            ) : null}
          </React.Fragment>
        ))}
      </span>
    </TradeLogSortableCell>
  );
}

export type MarketContextSituationsTableProps = {
  rows: ConditionAnalysisSituationRow[];
  onPickSituation?: (row: ConditionAnalysisSituationRow) => void;
  isPicked?: (row: ConditionAnalysisSituationRow) => boolean;
  metricDefinitions?: Record<string, unknown>;
  showManagementPreview?: boolean;
  onPreviewSituation?: (situation: string) => void;
  onPreviewSituationRow?: (row: ConditionAnalysisSituationRow) => void;
  previewActiveKey?: string | readonly string[];
  previewLoadingKey?: string | readonly string[];
  /** Matches `situation` column (e.g. from regime_actionable_filters evidence). */
  highlightSituation?: string | null;
  /** Bumps when Overview deep-links here — drives scroll + pulse highlight. */
  evidenceFocusNonce?: number;
  emptyMessage?: string;
};

function situationPreviewRowKey(row: ConditionAnalysisSituationRow): string {
  const situation = String(row.situation ?? "").trim();
  const sample = row.sample_size ?? "";
  return `situation:${situation}::${sample}`;
}

function createSituationColumns(props: MarketContextSituationsTableProps): ColumnDef<ConditionAnalysisSituationRow>[] {
  const showPickColumn = Boolean(props.onPickSituation);
  const showManagementPreview = Boolean(props.showManagementPreview && props.onPreviewSituation);
  const showPreviewTrades = Boolean(props.onPreviewSituationRow);

  const cols: ColumnDef<ConditionAnalysisSituationRow>[] = [
    {
      id: "situation",
      accessorFn: (row) => String(row.situation ?? ""),
      header: ({ column }) => (
        <MarketContextMetricColumnHeader column={column} title="Situation" align="left" />
      ),
      cell: ({ row }) => (
        <SituationLabelCell row={row.original} metricDefinitions={props.metricDefinitions} />
      ),
    },
  ];

  if (showPickColumn) {
    cols.push({
      id: "simulatorPick",
      enableSorting: false,
      header: () => <AddToSimulatorColumnHeader />,
      cell: ({ row }) => (
        <MarketContextActionColumnCell>
          <div data-market-context-simulator-pick-cell="">
            <SimulatorPickActionCell
              picked={Boolean(props.isPicked?.(row.original))}
              addAriaLabel="Add situation to picks"
              onAdd={() => {
                props.onPickSituation?.(row.original);
              }}
            />
          </div>
        </MarketContextActionColumnCell>
      ),
    });
  }

  cols.push(
    {
      id: "sample",
      accessorFn: (row) => row.sample_size ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Sample"
          tooltip="All trades in this row, including scratch/breakeven."
        />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtInt(row.original.sample_size)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "winRate",
      accessorFn: (row) => row.win_rate ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader column={column} title="Win rate" tooltip={WIN_RATE_COLUMN_TOOLTIP} />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtPct(row.original.win_rate)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "expectancy",
      accessorFn: (row) => row.expectancy ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Expectancy"
          tooltip="Average expected return per trade for the situation."
        />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtNum(row.original.expectancy)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "totalPnl",
      accessorFn: (row) => row.total_pnl ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Total PnL"
          tooltip="Total profit and loss across all trades in the situation."
        />
      ),
      cell: ({ row }) => {
        const totalPnl = row.original.total_pnl;
        if (totalPnl == null || !Number.isFinite(totalPnl)) {
          return (
            <TradeLogSortableCell align="right" className="font-medium tabular-nums">
              —
            </TradeLogSortableCell>
          );
        }
        return (
          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
            <span data-trade-log-pnl="" style={{ color: pnlBarFill(totalPnl) }}>
              {fmtPnl(totalPnl)}
            </span>
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "quality",
      accessorFn: (row) => row.quality_score ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Quality"
          tooltip="Backend quality score for this situation ranking."
        />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {row.original.quality_score == null ? "—" : fmtNum(row.original.quality_score)}
        </TradeLogSortableCell>
      ),
    },
  );

  if (showManagementPreview) {
    cols.push({
      id: "managementPreview",
      enableSorting: false,
      header: () => (
        <div className="flex h-8 items-center justify-end px-3 font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
          Management
        </div>
      ),
      cell: ({ row }) => {
        const situation = String(row.original.situation ?? "").trim();
        if (!situation || situation === "—") return <div className="py-1 pr-1" />;
        return (
          <div className="flex justify-end py-1 pr-1">
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="h-8 shrink-0 px-5 active:translate-y-0"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                props.onPreviewSituation?.(situation);
              }}
            >
              <span className="uppercase tracking-wide">Preview</span>
            </Button>
          </div>
        );
      },
    });
  }

  if (showPreviewTrades) {
    cols.push({
      id: "previewTrades",
      enableSorting: false,
      header: () => <MarketContextPreviewTradesColumnHeader />,
      cell: ({ row }) => {
        const rowKey = situationPreviewRowKey(row.original);
        const disabled =
          typeof row.original.sample_size === "number" && row.original.sample_size <= 0;
        return (
          <MarketContextPreviewTradesButton
            rowKey={rowKey}
            activeKey={props.previewActiveKey}
            loadingKey={props.previewLoadingKey}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              props.onPreviewSituationRow?.(row.original);
            }}
          />
        );
      },
    });
  }

  return cols;
}

export function MarketContextSituationsTable(props: MarketContextSituationsTableProps): React.ReactElement {
  const columns = useMemo(
    () => createSituationColumns(props),
    [props.isPicked, props.metricDefinitions, props.onPickSituation, props.onPreviewSituation, props.onPreviewSituationRow, props.previewActiveKey, props.previewLoadingKey, props.showManagementPreview],
  );

  const highlightRowId = useMemo(() => {
    const needle = props.highlightSituation?.trim();
    if (!needle) return undefined;
    const match = props.rows.find((r) => String(r.situation ?? "").trim() === needle);
    return match ? situationRowId(match) : undefined;
  }, [props.highlightSituation, props.rows]);

  return (
    <div data-market-context-situations-table="" className="w-full min-w-0 rounded-[6px]">
      <DataTable
        columns={columns}
        data={props.rows}
        getRowId={(row) => situationRowId(row)}
        activeRowId={highlightRowId}
        getRowDataAttributes={(row) => {
          const situation = String(row.situation ?? "").trim();
          const attrs: Record<string, string> = {};
          if (situation) attrs["data-market-context-situation"] = situation;
          if (
            props.evidenceFocusNonce &&
            highlightRowId &&
            situationRowId(row) === highlightRowId
          ) {
            // Drive pulse via attribute only — do not remount the row (remount drops mid-click).
            attrs["data-market-context-evidence-highlight"] = String(props.evidenceFocusNonce);
          }
          return Object.keys(attrs).length ? attrs : undefined;
        }}
        showPagination={false}
        emptyMessage={props.emptyMessage ?? "No situations available for this view."}
        className="min-w-0"
      />
    </div>
  );
}
